/**
 * 趋势折线图主组件
 * 对应板块: 板块 C — 趋势折线图
 */
import React, { useState, useMemo, useCallback } from 'react';
import useLanguage from '@op/hooks/useLanguage';
import { Segment } from 'tea-component';
import SingleSelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import CardCommon from '@op/components/CardCommon';
import { barIcon, lineIcon, tableIcon } from '@op/constants/chart';
import TrendLineChart from './TrendLineChart';
import TrendTable, { buildTableRows } from './TrendTable';
import { useTimelineData } from '../../hooks/useTimelineData';
import { getChartMetricLabelKey } from '../../utils/metricLabels';
import type { IDownloadConfig } from '../../hooks/useTimelineData';
import type {
  ITrendChartProps,
  TTrendMetric,
  TChartType,
  TCrossAnalysisMetric,
} from './types';
import type { operation } from '@op/components/CardCommon';
import './index.scss';


const chartHeight = 316;

/** 统一指标配置 —— 修改文案只需改此处 */
const METRIC_CONFIG = [
  { value: 'all_creative' as TTrendMetric, baseKey: 'Active Ads', requestMetric: 'creatives_trend' },
  { value: 'new_creative' as TTrendMetric, baseKey: 'New Ads', requestMetric: 'creatives_trend' },
  { value: 'all_material' as TTrendMetric, baseKey: 'Active Creatives', requestMetric: 'creatives_trend' },
  { value: 'new_material' as TTrendMetric, baseKey: 'New Creatives', requestMetric: 'creatives_trend' },
  { value: 'engagement_stat' as TTrendMetric, baseKey: 'Total Engagements on Active Ads', requestMetric: 'engagement_stat' },
];

const TrendChart: React.FC<ITrendChartProps> = ({ filter, gameInfo }) => {
  const { formatMessage } = useLanguage();

  // ==================== 状态管理 ====================
  const [metric, setMetric] = useState<TTrendMetric>('all_creative');
  const [chartType, setChartType] = useState<TChartType>('bar');
  // 交叉分析默认值：mobile → download，其他 → steam_pcu
  const defaultCrossAnalysis =
    gameInfo?.entity_type === 'mobile' ? 'download' : 'steam_pcu';
  const [crossAnalysis, setCrossAnalysis] =
    useState<TCrossAnalysisMetric>(defaultCrossAnalysis);
  const [requestMetric, setRequestMetric] = useState<string>('creatives_trend');

  // ==================== 指标选项（根据 date_type 动态变化） ====================
  const metricOptions = useMemo(() =>
    METRIC_CONFIG.map(({ value, baseKey, requestMetric: reqMetric }) => ({
      value,
      text: formatMessage(getChartMetricLabelKey(baseKey, filter)),
      metric: reqMetric,
    })),
    [formatMessage, filter]);

  // ==================== 下载配置 ====================
  const metricText = metricOptions.find((opt) => opt.value === metric)?.text || metric;
  const downloadConfig = useMemo<IDownloadConfig>(() => ({
    transformTableData: (tableRecord, { extraData }) => {
      const mainSeries = extraData?.filteredSeries || [];
      const xAxis = extraData?.filteredXAxis || [];
      const crossSeries = extraData?.series || [];
      if (!xAxis?.length) {
        return tableRecord;
      }
      return buildTableRows(xAxis, [mainSeries, crossSeries]);
    },
    fileName: `Ad Creatives ${metricText}`,
  }), [metricText]);

  // ==================== 数据请求 ====================
  const {
    chartData,
    compareChartData,
    crossAnalysisData,
    loading,
    isEmpty,
    download,
  } = useTimelineData({
    filter,
    gameInfo,
    metric: requestMetric as any,
    topN: 100,
    needCompareTimeline: true,
    downloadConfig,
  });

  // ==================== 按指标过滤数据 ====================
  const filteredData = useMemo(() => {
    if (!chartData?.series?.length) return null;

    const filteredSeries = chartData.series.filter(
      (s) => s.name === metric,
    );

    return {
      ...chartData,
      series: filteredSeries,
      legend: [metric],
    };
  }, [chartData, metric]);

  // ==================== 交叉分析数据过滤 ====================
  const filteredCrossData = useMemo(() => {
    if (!crossAnalysis || !crossAnalysisData?.series?.length) return undefined;

    const filteredSeries = crossAnalysisData.series.filter(
      (s) => s.name === crossAnalysis,
    );

    if (!filteredSeries.length) return undefined;

    return {
      ...crossAnalysisData,
      series: filteredSeries,
      legend: [crossAnalysis],
    };
  }, [crossAnalysisData, crossAnalysis]);


  // 指标切换处理
  const handleMetricChange = (value: TTrendMetric) => {
    const newMetric = METRIC_CONFIG.find((c) => c.value === value)?.requestMetric || 'creatives_trend';

    if (newMetric !== requestMetric) {
      setRequestMetric(newMetric);
    }

    setMetric(value);
  };

  // ==================== 交叉分析选项（根据 date_type 动态变化） ====================
  const crossAnalysisOptions = useMemo(() => {
    const gameType = gameInfo?.entity_type || 'mobile';

    if (gameType === 'mobile') {
      return [
        { value: 'download', text: getChartMetricLabelKey('Download', filter) },
        { value: 'dau', text: getChartMetricLabelKey('DAU', filter) },
      ];
    }

    return [
      { value: 'steam_pcu', text: 'Steam PCU' },
      { value: 'steam_revenue', text: 'Steam Revenue' },
    ];
  }, [gameInfo, filter]);

  // ==================== Title 区域 ====================
  const titleElement = (
    <div className="dbo-trend-chart__title">
      <Segment
        options={metricOptions}
        value={metric}
        onChange={(value) => handleMetricChange(value as TTrendMetric)}
      />
      <SingleSelect
        key="crossAnalysis"
        defaultValue={crossAnalysis}
        onChange={(_: any, value: string) =>
          setCrossAnalysis(value as TCrossAnalysisMetric)
        }
        defaultParams={{
          options: crossAnalysisOptions,
          btnTextPrefix: formatMessage('Cross-Analysis'),
          needFormatMessage: true,
          matchButtonWidth: true,
        }}
      />
    </div>
  );

  // ==================== 翻译文案 ====================
  const metricLabel = metricText;
  const crossAnalysisLabel = formatMessage(
    crossAnalysisOptions.find((opt) => opt.value === crossAnalysis)?.text ||
    crossAnalysis,
  );

  // ==================== 操作按钮配置 ====================
  const operations: operation[] = [lineIcon, barIcon, tableIcon];

  const handleOperationsOnClick = (item: operation) => {
    if (item.symbol) {
      setChartType(item.symbol as TChartType);
    }
  };

  const handleTrendDownload = useCallback(() => {
    if (!download || !filteredData?.xAxis?.length) return;

    const mainHeader = getChartMetricLabelKey(
      METRIC_CONFIG.find((c) => c.value === metric)?.baseKey || metric,
      filter,
    );
    const crossHeader =
      crossAnalysisOptions.find((opt) => opt.value === crossAnalysis)?.text ||
      crossAnalysis;

    const legend = [
      ...(filteredData.legend || []),
      ...(filteredCrossData?.series || []).map((s) => s.name),
    ];
    const columnHeaders: Record<string, string> = {};
    (filteredData.legend || []).forEach((name) => {
      columnHeaders[name] = mainHeader;
    });
    (filteredCrossData?.series || []).forEach((s) => {
      columnHeaders[s.name] = crossHeader;
    });
    download({
      extraData: {
        filteredSeries: filteredData.series,
        filteredXAxis: filteredData.xAxis,
        series: filteredCrossData?.series,
      },
      legend,
      columnHeaders,
    });
  }, [
    download,
    filteredCrossData,
    filteredData,
    metric,
    filter,
    crossAnalysis,
    crossAnalysisOptions,
  ]);

  // ==================== 渲染逻辑 ====================
  const renderContent = () => {
    if (chartType === 'table') {
      return (
        <TrendTable
          data={filteredData!}
          height={chartHeight}
          metricLabel={metricLabel}
          crossAnalysisLabel={crossAnalysisLabel}
          crossAnalysisData={filteredCrossData}
          filter={filter}
        />
      );
    }

    return (
      <TrendLineChart
        data={filteredData!}
        compareData={compareChartData}
        metric={metric}
        metricLabel={metricLabel}
        chartType={chartType}
        filter={filter}
        height={chartHeight}
        crossAnalysisMetric={crossAnalysis}
        crossAnalysisLabel={crossAnalysisLabel}
        crossAnalysisData={filteredCrossData}
      />
    );
  };

  return (
    <CardCommon
      className="dbo-trend-chart"
      title={titleElement}
      operations={operations}
      defaultOperation={chartType}
      handleOperationsOnClick={handleOperationsOnClick}
      hasDownload
      handleDownloadOnClick={handleTrendDownload}
      loading={loading}
      empty={isEmpty}
      emptyHeight={chartHeight}
      loadingHeight={chartHeight}
    >
      {renderContent()}
    </CardCommon>
  );
};

export default React.memo(TrendChart);
